import { Tile, type Overworld, type Vec, idx } from "../sim/types";

const W = 84;
const H = 52;

function fill(tiles: Uint8Array, t: number): void {
  tiles.fill(t);
}

function rect(tiles: Uint8Array, x: number, y: number, w: number, h: number, t: number): void {
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const xx = x + i;
      const yy = y + j;
      if (xx >= 0 && yy >= 0 && xx < W && yy < H) tiles[idx(xx, yy, W)] = t;
    }
  }
}

function stamp(tiles: Uint8Array, x: number, y: number, rows: string[], legend: Record<string, number>): void {
  for (let j = 0; j < rows.length; j++) {
    const row = rows[j]!;
    for (let i = 0; i < row.length; i++) {
      const ch = row[i]!;
      const t = legend[ch];
      if (t === undefined) continue;
      const xx = x + i;
      const yy = y + j;
      if (xx >= 0 && yy >= 0 && xx < W && yy < H) tiles[idx(xx, yy, W)] = t;
    }
  }
}

function scatterTrees(tiles: Uint8Array, x: number, y: number, w: number, h: number, step: number): void {
  for (let j = 0; j < h; j += step) {
    for (let i = 0; i < w; i += step) {
      const xx = x + i + ((j * 3) % 2);
      const yy = y + j;
      if (xx >= 0 && yy >= 0 && xx < W && yy < H) tiles[idx(xx, yy, W)] = Tile.Tree;
    }
  }
}

export function buildOverworld(): Overworld {
  const tiles = new Uint8Array(W * H);
  fill(tiles, Tile.Grass);

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < 7; x++) {
      if ((x + y * 3) % 5 !== 0) tiles[idx(x, y, W)] = Tile.Water;
    }
  }
  for (let y = 20; y < 32; y++) tiles[idx(7, y, W)] = Tile.Stone;

  scatterTrees(tiles, 1, 1, W - 2, 5, 2);
  scatterTrees(tiles, 1, H - 5, W - 2, 4, 2);
  scatterTrees(tiles, W - 6, 8, 5, H - 14, 2);
  scatterTrees(tiles, 16, 8, 10, 8, 3);
  rect(tiles, 8, 10, 4, 8, Tile.Tall);

  rect(tiles, 30, 22, 20, 11, Tile.Dirt);
  for (let x = 18; x < 70; x++) {
    tiles[idx(x, 28, W)] = Tile.Path;
    tiles[idx(x, 29, W)] = Tile.Path;
  }
  for (let y = 14; y < 38; y++) {
    tiles[idx(38, y, W)] = Tile.Path;
    tiles[idx(39, y, W)] = Tile.Path;
  }

  stamp(tiles, 32, 22, ["########", "#======#", "#======#", "##++####"], {
    "#": Tile.Wall,
    "=": Tile.Floor,
    "+": Tile.Door,
  });
  stamp(tiles, 27, 20, ["#####", "#===#", "##+##"], { "#": Tile.Wall, "=": Tile.Floor, "+": Tile.Door });
  stamp(tiles, 52, 26, ["######", "#====#", "#====#", "##++##"], { "#": Tile.Wall, "=": Tile.Floor, "+": Tile.Door });
  stamp(tiles, 58, 27, ["########", "#======#", "#======#", "###++###"], {
    "#": Tile.Wall,
    "=": Tile.Wood,
    "+": Tile.Door,
  });

  const homeDoor: Vec = { x: 61, y: 30 };
  const homeOrigin: Vec = { x: 58, y: 27 };
  rect(tiles, 64, 31, 5, 4, Tile.Soil);

  stamp(tiles, 42, 16, ["#####", "#R.R#", "##+##"], { "#": Tile.Ruin, R: Tile.Ruin, ".": Tile.Dirt, "+": Tile.Door });

  stamp(tiles, 18, 33, ["######", "#RRRR#", "#R..R#", "##++##"], {
    "#": Tile.Ruin,
    R: Tile.Stone,
    ".": Tile.Dirt,
    "+": Tile.Door,
  });

  const well: Vec = { x: 38, y: 24 };
  tiles[idx(well.x, well.y, W)] = Tile.Well;
  tiles[idx(well.x + 1, well.y, W)] = Tile.Stone;
  tiles[idx(well.x, well.y + 1, W)] = Tile.Stone;
  tiles[idx(36, 26, W)] = Tile.Stone;

  const trail: Array<[number, number]> = [
    [44, 18],
    [43, 19],
    [42, 20],
    [41, 21],
    [40, 22],
    [45, 19],
    [46, 17],
    [48, 16],
    [50, 15],
  ];
  for (const [x, y] of trail) tiles[idx(x, y, W)] = Tile.Dirt;

  stamp(tiles, 48, 38, ["###", "#.#", "###"], { "#": Tile.Stone, ".": Tile.Vein });

  const store: Vec = { x: 35, y: 25 };
  const wake: Vec = { x: 42.4, y: 28.6 };
  const pois: Record<string, Vec> = {
    june: { x: 44, y: 18 },
    pump: { x: 20, y: 35 },
    stake: { x: 36, y: 26 },
    mouth: { x: 49, y: 39 },
    garden: { x: 66, y: 33 },
  };

  const crops = [
    { x: 64, y: 31, kind: "root" as const, stage: 1.2, water: 0.5, contamination: 0, plantedAt: 0 },
    { x: 65, y: 31, kind: "root" as const, stage: 0.4, water: 0.35, contamination: 0, plantedAt: 0 },
    { x: 66, y: 31, kind: "bitter" as const, stage: 2.1, water: 0.6, contamination: 0, plantedAt: 0 },
    { x: 64, y: 32, kind: "root" as const, stage: 0, water: 0.2, contamination: 0, plantedAt: 0 },
    { x: 65, y: 32, kind: "bitter" as const, stage: 1.6, water: 0.4, contamination: 0.05, plantedAt: 0 },
    { x: 66, y: 32, kind: "root" as const, stage: 0.2, water: 0.3, contamination: 0, plantedAt: 0 },
  ];
  for (const c of crops) tiles[idx(c.x, c.y, W)] = c.stage >= 1 ? Tile.Crop : Tile.Soil;

  return { w: W, h: H, tiles, well, homeDoor, homeOrigin, store, wake, pois, crops };
}

export function homeInterior(): { w: number; h: number; tiles: Uint8Array; door: Vec } {
  const w = 12;
  const h = 10;
  const tiles = new Uint8Array(w * h);
  tiles.fill(Tile.Wall);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) tiles[y * w + x] = Tile.Wood;
  }
  tiles[8 * w + 6] = Tile.Door;
  tiles[8 * w + 5] = Tile.Door;
  return { w, h, tiles, door: { x: 6, y: 8 } };
}
